import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';

const SplashPage = () => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleEnter = () => {
    setIsExiting(true);
    setTimeout(() => navigate('/accueil'), 800);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: isExiting ? 0 : 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-0 z-[100] overflow-hidden"
      >
        {/* Background - Light elegant gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FAFAFA] via-[#F5F0E8] to-[#EDE4D8]">
          
          {/* Decorative orbs */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: isLoaded ? 0.6 : 0, scale: isLoaded ? 1 : 0.8 }}
            transition={{ duration: 2, ease: "easeOut" }}
            className="absolute top-1/4 -left-1/4 w-[800px] h-[800px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(91, 45, 142, 0.08) 0%, transparent 70%)',
              filter: 'blur(80px)'
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: isLoaded ? 0.5 : 0, scale: isLoaded ? 1 : 0.8 }}
            transition={{ duration: 2, delay: 0.3, ease: "easeOut" }}
            className="absolute bottom-1/4 -right-1/4 w-[600px] h-[600px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(247, 201, 72, 0.15) 0%, transparent 70%)',
              filter: 'blur(100px)'
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: isLoaded ? 0.4 : 0, scale: isLoaded ? 1.2 : 0.8 }}
            transition={{ duration: 3, delay: 0.5, ease: "easeOut" }}
            className="absolute top-1/3 right-1/4 w-[500px] h-[500px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(13, 148, 136, 0.1) 0%, transparent 70%)',
              filter: 'blur(100px)'
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
          
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 30 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8"
          >
            <img
              src="/logo-ada.png"
              alt="ADA Logo"
              className="h-28 sm:h-36 w-auto drop-shadow-lg"
            />
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#5B2D8E]/5 border border-[#5B2D8E]/10 text-[#5B2D8E] text-xs font-medium tracking-[0.2em] uppercase">
              Alliance Démocratique Africaine
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 40 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-8"
          >
            <h1 className="text-5xl sm:text-7xl lg:text-8xl xl:text-9xl font-black tracking-tighter text-[#1A0F2E] leading-[0.9]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="block">Une Nation.</span>
              <span className="block bg-gradient-to-r from-[#5B2D8E] to-[#7B4AAE] bg-clip-text text-transparent">
                Un avenir.
              </span>
            </h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#5B2D8E]/60 text-lg sm:text-xl text-center max-w-xl mb-12"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Constructive. Contributive. Avançant fermement.
          </motion.p>

          {/* Enter Button */}
          <motion.button
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            onClick={handleEnter}
            className="group relative overflow-hidden px-12 py-5 rounded-sm bg-[#5B2D8E] text-white font-bold text-lg tracking-wide transition-all duration-500 hover:scale-105 hover:shadow-[0_20px_60px_rgba(91,45,142,0.3)]"
            data-testid="splash-enter-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="relative z-10 flex items-center gap-3">
              Entrer
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </motion.button>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isLoaded ? 0.5 : 0 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          >
            <span className="text-[#5B2D8E]/30 text-xs tracking-widest uppercase">Côte d'Ivoire</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <ChevronDown className="w-5 h-5 text-[#5B2D8E]/30" />
            </motion.div>
          </motion.div>

          {/* Corner decorations */}
          <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-[#5B2D8E]/10" />
          <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-[#5B2D8E]/10" />
          <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-[#5B2D8E]/10" />
          <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-[#5B2D8E]/10" />

          {/* Side text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isLoaded ? 0.3 : 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="hidden lg:block absolute left-8 top-1/2 -translate-y-1/2"
          >
            <span 
              className="text-[#5B2D8E]/20 text-xs tracking-[0.3em] uppercase"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            >
              ADA · Parti politique depuis 2024
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isLoaded ? 0.3 : 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2"
          >
            <span 
              className="text-[#5B2D8E]/20 text-xs tracking-[0.3em] uppercase"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
            >
              Refonder · Libérer · Construire
            </span>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SplashPage;
